import React, { useMemo } from "react";
import "./PrivacyPolicy.css";
import { useLocalContext } from "../../context/LocalContext";

const PrivacyPolicy = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = useMemo(
    () => ({
      name: webinfo.name || "Your Brand",
      email: webinfo.email || "hello@example.com",
      phone: webinfo.phone || "+91 80000 00000",
      phonecall: webinfo.phonecall || "+918000000000",
      address: webinfo.address || "Remote-first • Serving clients across cities",
    }),
    [webinfo]
  );

  const effectiveDate = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  const sections = [
    { id: "overview", label: "Overview & scope", index: "01" },
    { id: "data-we-collect", label: "Data we collect", index: "02" },
    { id: "how-we-use-data", label: "How we use data", index: "03" },
    { id: "legal-bases", label: "Lawful bases", index: "04" },
    { id: "cookies", label: "Cookies & tracking", index: "05" },
    { id: "sharing", label: "Sharing & processors", index: "06" },
    { id: "transfers", label: "International transfers", index: "07" },
    { id: "retention", label: "Data retention", index: "08" },
    { id: "rights", label: "Your rights", index: "09" },
    { id: "security", label: "Security", index: "10" },
    { id: "children", label: "Children’s data", index: "11" },
    { id: "changes-contact", label: "Changes & contact", index: "12" },
  ];

  return (
    <section className="privacy-policy">
      <div className="pp-wrap">
        {/* Header */}
        <header className="privacy-policy-header">
          <p className="pp-eyebrow">Privacy & data use</p>
          <div className="pp-header-main">
            <div>
              <h1>Privacy Policy</h1>
              <p>
                At {brand.name}, we know that sharing your details is a matter of trust. This page
                explains, in simple language, what happens when you fill out a form, click on our
                ads, or browse our website — and how we look after that information.
              </p>
            </div>
            <div className="pp-header-meta-card">
              <div className="meta-row">
                <span className="meta-label">Data controller</span>
                <span className="meta-value">{brand.name}</span>
              </div>
              <div className="meta-row">
                <span className="meta-label">Last updated</span>
                <span className="meta-value">{effectiveDate}</span>
              </div>
            </div>
          </div>
        </header>

        {/* Layout: sidebar nav + content */}
        <div className="pp-layout">
          {/* Outline / sidebar */}
          <aside className="pp-nav" aria-label="Privacy policy outline">
            <h2 className="pp-nav-title">On this page</h2>
            <ol className="pp-nav-list">
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>
                    <span className="pp-nav-index">{s.index}</span>
                    <span className="pp-nav-label">{s.label}</span>
                  </a>
                </li>
              ))}
            </ol>
            <div className="pp-nav-footnote">
              <p>
                This is a human-friendly summary of how we use personal data on our website and in
                our digital marketing work. It sits alongside any detailed legal terms we may sign
                with you for a specific project.
              </p>
            </div>
          </aside>

          {/* Main content card */}
          <div className="privacy-policy-content">
            <section id="overview">
              <h2>01. Overview &amp; scope</h2>
              <p>
                This Privacy Policy explains how {brand.name} (“we”, “us”, “our”) handles personal
                information when you:
              </p>
              <ul>
                <li>visit our website,</li>
                <li>click on or respond to our ads or campaigns, or</li>
                <li>contact us about services like websites, ads, SEO or other marketing help.</li>
              </ul>
              <p>
                By using our website or sharing your details with us, you agree that we can use your
                information as described here, in line with the laws that apply in your country.
              </p>
            </section>

            <section id="data-we-collect">
              <h2>02. Data we collect</h2>
              <p>We only collect information we need to respond to you and improve our services.</p>
              <ul>
                <li>
                  <strong>Details you share with us</strong> – when you fill in a contact form,
                  request a quote, message us on email or WhatsApp, or book a call. This usually
                  includes your name, email, phone number, business name, and a short description of
                  what you need help with.
                </li>
                <li>
                  <strong>Basic website &amp; usage data</strong> – for example which pages you
                  visit, how you found us (Google, social media, ad, etc.), your device type and
                  browser. This is typically collected through analytics tools.
                </li>
                <li>
                  <strong>Cookies and similar tools</strong> – small files that help the site run
                  properly, remember preferences, and give us simple stats so we can see what is
                  working (and what is not).
                </li>
              </ul>
              <p>
                We do <strong>not</strong> collect sensitive personal data (like financial records,
                ID proof, or health data) through this website.
              </p>
            </section>

            <section id="how-we-use-data">
              <h2>03. How we use your data</h2>
              <p>We mainly use your information to communicate with you and improve our work.</p>
              <ul>
                <li>
                  <strong>To reply to you</strong> – answering your questions, sending proposals,
                  scheduling calls, or following up on projects.
                </li>
                <li>
                  <strong>To deliver and improve our services</strong> – for example, keeping notes
                  about your project so we understand your goals and can provide better design,
                  ad/SEO strategy and support.
                </li>
                <li>
                  <strong>To understand our website performance</strong> – seeing which pages people
                  read, which services they click, and which campaigns bring useful traffic.
                </li>
                <li>
                  <strong>To share useful updates</strong> – occasionally sending you relevant info
                  about our services, if you’ve asked to hear from us or it makes sense based on our
                  existing relationship.
                </li>
                <li>
                  <strong>To keep things safe and compliant</strong> – preventing misuse or spam,
                  and keeping necessary records for tax, accounts and legal reasons.
                </li>
              </ul>
            </section>

            <section id="legal-bases">
              <h2>04. Legal bases for processing</h2>
              <p>
                Where data-protection laws like the GDPR or UK GDPR apply, we rely on one or more
                of the following reasons (legal bases) for using your data:
              </p>
              <ul>
                <li>
                  <strong>Consent</strong> – when you choose to send us your details (for example
                  via a form) or accept optional cookies.
                </li>
                <li>
                  <strong>Legitimate interest</strong> – for running our business in a normal way,
                  answering enquiries, improving our services, and measuring our marketing, where
                  this does not unfairly impact your privacy.
                </li>
                <li>
                  <strong>Contract</strong> – if we need your data to prepare or deliver a service
                  you have asked for (for example, to send a proposal or run a campaign for you).
                </li>
                <li>
                  <strong>Legal duty</strong> – where we must keep or share certain information to
                  follow the law (for example, basic invoice and payment records).
                </li>
              </ul>
            </section>

            <section id="cookies">
              <h2>05. Cookies &amp; tracking technologies</h2>
              <p>
                Our website may use cookies, pixels and similar tools from platforms such as Google
                Analytics or ad networks (like Google or Meta) to:
              </p>
              <ul>
                <li>help the site load and run correctly,</li>
                <li>measure which pages and services people are interested in, and</li>
                <li>understand which ads or campaigns are performing.</li>
              </ul>
              <p>
                Depending on your location, you might see a cookie banner that lets you accept or
                reject optional cookies. You can also manage cookies from your browser settings at
                any time. If you block some cookies, parts of the site may not work exactly as
                expected, but basic content should still be available.
              </p>
            </section>

            <section id="sharing">
              <h2>06. Sharing information with others</h2>
              <p>
                We do <strong>not</strong> sell or rent your personal data to third parties. We only
                share it when necessary to run our business or deliver your project. This can
                include:
              </p>
              <ul>
                <li>Website hosting and infrastructure providers.</li>
                <li>Analytics, CRM or email platforms we use to stay organised.</li>
                <li>Online meeting, scheduling and proposal tools.</li>
                <li>Payment, invoicing and accounting systems.</li>
                <li>
                  Trusted contractors or team members who help us deliver design, development or
                  marketing work.
                </li>
              </ul>
              <p>
                These service providers only receive the information needed to do their job and are
                expected to keep it secure and handle it in line with data-protection rules.
              </p>
            </section>

            <section id="transfers">
              <h2>07. International data transfers</h2>
              <p>
                Some of the tools we use (for example hosting, analytics or email platforms) may be
                based in other countries. This means your data might be stored or processed outside
                your own country.
              </p>
              <p>
                Where the law requires it, we aim to use appropriate safeguards (such as standard
                contractual clauses or similar protections offered by the provider) to help keep
                your data protected when it is handled in another country.
              </p>
            </section>

            <section id="retention">
              <h2>08. How long we keep data</h2>
              <p>
                We keep personal data only for as long as we need it for the reasons described in
                this policy, or for as long as the law requires us to keep it.
              </p>
              <p>For example, we may keep:</p>
              <ul>
                <li>
                  enquiry and project information while we are in active discussions or working
                  together, and
                </li>
                <li>
                  basic contract and billing information for a number of years for tax and accounting
                  purposes.
                </li>
              </ul>
              <p>
                When data is no longer needed, we aim to delete it safely or anonymise it so it can
                no longer be linked back to you.
              </p>
            </section>

            <section id="rights">
              <h2>09. Your rights and choices</h2>
              <p>
                Depending on where you live and which laws apply, you may have some or all of the
                following rights over your personal data:
              </p>
              <ul>
                <li>To ask what personal data we hold about you.</li>
                <li>To ask us to correct information that is wrong or incomplete.</li>
                <li>To ask us to delete certain information, in some situations.</li>
                <li>
                  To ask us to limit or stop using your data in certain ways (for example, for
                  direct marketing).
                </li>
                <li>To ask for a copy of your data in a portable format where applicable.</li>
              </ul>
              <p>
                If you want to use any of these rights, please contact us using the details in the
                last section. We may need to confirm your identity first. In some cases, we may not
                be able to fully meet your request (for example, where we must keep some data for
                legal or accounting reasons), but we will explain this clearly.
              </p>
            </section>

            <section id="security">
              <h2>10. How we protect your data</h2>
              <p>
                We use reasonable technical and organisational steps to help protect your data,
                including limiting access to tools, using secure connections where appropriate, and
                regularly reviewing which platforms we use.
              </p>
              <p>
                No website or online service can promise 100% security, but we aim to keep risks
                sensible and to work with reputable providers who take security seriously.
              </p>
            </section>

            <section id="children">
              <h2>11. Children’s data</h2>
              <p>
                Our website and services are aimed at adults and business owners, not children. We
                do not knowingly collect personal information from children under 13 (or under 16
                where local rules set a higher age).
              </p>
              <p>
                If you think a child has shared personal information with us by mistake, please
                contact us so we can review and remove it where appropriate.
              </p>
            </section>

            <section id="changes-contact">
              <h2>12. Changes to this policy &amp; how to contact us</h2>
              <p>
                From time to time, we may update this Privacy Policy if our services, tools or legal
                requirements change. When that happens, we will update the “Last updated” date at
                the top of this page, and may provide extra notice if the changes are significant.
              </p>
              <p>
                If you have any questions, concerns or requests about this policy or how we handle
                your data, you can reach us at:
              </p>
              <ul className="pp-contact-list">
                <li>
                  <strong>Email:</strong>{" "}
                  <a href={`mailto:${webinfo.email || "hello@example.com"}`}>
                    {brand.email}
                  </a>
                </li>
                <li>
                  <strong>Phone:</strong>{" "}
                  <a href={`tel:${webinfo.phonecall || "+918000000000"}`}>
                    {brand.phone}
                  </a>
                </li>
                <li>
                  <strong>Address:</strong> {brand.address}
                </li>
              </ul>
            </section>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
